// pages/department/View.tsx
import { ErrorMessage, Spinner, CustomAlertDialog } from "@/components";
import DetailView from "@/components/DetailView";
import DegreeDialog from "@/components/formDialog/DegreeDialog";
import DepartmentDialog from "@/components/formDialog/DepartmentDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useProgramBatchesQuery } from "@/hooks/api/batch.queries";
import {
  useDepartmentByIdQuery,
  useDepartmentProgramsQuery,
  useDeleteDepartmentMutation,
} from "@/hooks/api/department.queries";
import { useFormDialog, useAlertDialog } from "@/hooks/useDialogState";
import { BatchType } from "@/services/batch.service";
import { DepartmentType } from "@/services/department.service";
import { useAuthStore } from "@/store";
import {
  ChevronDown,
  ChevronUp,
  GraduationCap,
  Pencil,
  Plus,
  Trash,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// ─── DegreeCard ───────────────────────────────────────────────────────────────

interface DegreeCardProps {
  degree: any;
  canUpdate: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onNavigate: (path: string) => void;
}

const DegreeCard = ({
  degree,
  canUpdate,
  isExpanded,
  onToggleExpand,
  onEdit,
  onNavigate,
}: DegreeCardProps) => {
  const { data: batches = [], isLoading } = useProgramBatchesQuery(
    degree.id,
    isExpanded
  );

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between p-4">
        <div className="flex flex-col">
          <span className="text-xs font-bold text-gray-900">{degree.name}</span>
          <span className="text-[10px] text-gray-400">Code: {degree.code}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">
            Active
          </Badge>
          {canUpdate && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-gray-400 hover:text-blue-600"
              onClick={onEdit}
              aria-label="Edit degree"
            >
              <Pencil size={12} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-gray-400 hover:text-blue-600"
            onClick={onToggleExpand}
            aria-label={isExpanded ? "Hide batches" : "Show batches"}
          >
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-50 px-4 pb-3">
          {isLoading ? (
            <div className="flex justify-center py-3">
              <Spinner />
            </div>
          ) : batches.length > 0 ? (
            <div className="mt-2 space-y-1">
              {batches.map((batch: BatchType) => (
                <div
                  key={batch.id}
                  onClick={() => onNavigate(`/batches/${batch.id}`)}
                  className="flex cursor-pointer items-center justify-between rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-50"
                >
                  <div>
                    <p className="text-xs font-medium text-gray-800">
                      {batch.name}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      Sem {batch.current_semester} • {batch.start_year}–
                      {batch.end_year}
                    </p>
                  </div>
                  <span className="text-[10px] font-medium capitalize text-blue-600">
                    {batch.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-3 text-center text-[11px] text-gray-400">
              No batches found for this degree.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── DepartmentView ───────────────────────────────────────────────────────────

const DepartmentView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const canUpdate = user?.permissions?.includes("department.update");
  const canDelete = user?.permissions?.includes("department.delete");

  const {
    data: dept,
    isLoading,
    isError,
    refetch,
  } = useDepartmentByIdQuery(id || "");
  const { data: degrees = [], isLoading: isLoadingDegrees } =
    useDepartmentProgramsQuery(id || "");

  const deleteMutation = useDeleteDepartmentMutation({});
  const deleteDialog = useAlertDialog();

  const degreeDialog = useFormDialog<{
    mode?: "create" | "edit";
    programId?: string;
    programData?: { degree_id: string; name: string; code: string };
  }>({});

  const deptDialog = useFormDialog<{
    mode: "create" | "edit";
    id: string;
    data: DepartmentType | null;
  }>({ mode: "create", id: "", data: null });

  const [expandedProgramId, setExpandedProgramId] = useState<string | null>(
    null
  );

  if (isLoading)
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  if (isError || !dept)
    return (
      <ErrorMessage
        message="Failed to load department details."
        onRetry={() => refetch()}
      />
    );

  const handleDelete = () => {
    if (!id) return;
    deleteMutation.mutate(id, {
      onSuccess: () => navigate("/department"),
      onSettled: () => deleteDialog.close(),
    });
  };

  return (
      <DetailView
      title="Department"
      backPath="/department"
      actions={
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
          {canUpdate && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs flex-1 sm:flex-none"
              onClick={() =>
                deptDialog.open({ mode: "edit", id: dept.id, data: dept })
              }
            >
              Edit Department
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-red-200 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 flex-1 sm:flex-none"
              onClick={() => {
                deleteDialog.open({
                  action: "delete",
                  mantraId: dept.id,
                  mantraTitle: dept.name,
                  message: (
                    <span>
                      Are you sure you want to delete{" "}
                      <strong className="text-gray-800">{dept.name}</strong>? This
                      action cannot be undone.
                    </span>
                  ),
                });
              }}
            >
              <Trash size={14} className="mr-1.5" />
              Delete
            </Button>
          )}
        </div>
      }
    >
      {/* Profile Card */}
      <Card className="border-gray-100 shadow-sm">
        <CardContent className="pb-8 pt-8">
          <div className="flex flex-col gap-6 md:flex-row">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-2xl font-bold text-white">
              {dept.code}
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900">
                  {dept.name}
                </h2>
                <Badge
                  variant={dept.is_active ? "secondary" : "destructive"}
                  className="rounded-full px-3 font-medium"
                >
                  {dept.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
              <dl className="grid grid-cols-1 gap-x-12 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {dept.email && (
                  <div className="flex flex-col gap-0.5">
                    <dt className="text-xs font-medium text-gray-400">Email</dt>
                    <dd className="text-gray-600 break-all">📧 {dept.email}</dd>
                  </div>
                )}
                {dept.phone && (
                  <div className="flex flex-col gap-0.5">
                    <dt className="text-xs font-medium text-gray-400">Phone</dt>
                    <dd className="text-gray-600">📞 {dept.phone}</dd>
                  </div>
                )}
                <div className="flex flex-col gap-0.5">
                  <dt className="text-xs font-medium text-gray-400">
                    Department Code
                  </dt>
                  <dd className="text-gray-700">{dept.code}</dd>
                </div>
                {dept.established_year && (
                  <div className="flex flex-col gap-0.5">
                    <dt className="text-xs font-medium text-gray-400">
                      Established
                    </dt>
                    <dd className="text-gray-700">{dept.established_year}</dd>
                  </div>
                )}
                <div className="flex flex-col gap-0.5">
                  <dt className="text-xs font-medium text-gray-400">
                    Degree Programs
                  </dt>
                  <dd className="text-gray-700">{degrees.length}</dd>
                </div>
              </dl>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* HOD Card */}
      <Card className="border-gray-100 shadow-sm">
        <CardContent className="pb-6 pt-6">
          <p className="mb-4 text-sm font-semibold text-gray-700">
            Head of Department
          </p>
          {dept.hod_name ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-blue-50 bg-blue-50/30 p-4">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white shrink-0">
                  {dept.hod_name
                    .split(" ")
                    .map((n: string) => n[0])
                    .join("")
                    .toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {dept.hod_name}
                  </p>
                  <p className="text-xs text-gray-500 break-all">
                    {dept.email || "No email provided"}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto h-8 bg-white text-xs"
                onClick={() =>
                  dept.hod_id && navigate(`/people/staff/${dept.hod_id}`)
                }
              >
                View Profile
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center rounded-xl border border-dashed border-gray-200 p-8 text-sm text-gray-400">
              No HOD assigned to this department.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Degrees Card */}
      <Card className="border-gray-100 shadow-sm">
        <CardContent className="pb-6 pt-6">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">
              Associated Degrees
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-2 border-dashed text-xs"
              onClick={() => degreeDialog.open({ mode: "create" })}
            >
              <Plus size={14} /> Add Degree
            </Button>
          </div>
          <p className="mb-3 text-xs text-gray-400">
            Click <ChevronDown size={12} className="inline" /> to view batches.
          </p>
          {isLoadingDegrees ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : degrees.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {degrees.map((degree: any) => (
                <DegreeCard
                  key={degree.id}
                  degree={degree}
                  canUpdate={!!canUpdate}
                  isExpanded={expandedProgramId === degree.id}
                  onToggleExpand={() =>
                    setExpandedProgramId(
                      expandedProgramId === degree.id ? null : degree.id
                    )
                  }
                  onEdit={() =>
                    degreeDialog.open({
                      mode: "edit",
                      programId: degree.id,
                      programData: {
                        degree_id: degree.degree_id,
                        name: degree.name,
                        code: degree.code,
                      },
                    })
                  }
                  onNavigate={navigate}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-10">
              <GraduationCap size={40} className="text-gray-200" />
              <p className="mt-2 text-sm text-gray-400">
                No degrees associated with this department.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <DegreeDialog
        mode={degreeDialog.state.mode || "create"}
        programId={degreeDialog.state.programId}
        programData={degreeDialog.state.programData}
        isOpen={degreeDialog.state.isOpen}
        onOpenChange={(open) => !open && degreeDialog.close()}
        deptId={id || ""}
        onCancel={degreeDialog.close}
      />

      <DepartmentDialog
        mode={deptDialog.state.mode as "create" | "edit"}
        isOpen={deptDialog.state.isOpen}
        onOpenChange={(open) => !open && deptDialog.close()}
        filters={{}}
        deptId={deptDialog.state.id}
        deptData={deptDialog.state.data}
        onCancel={deptDialog.close}
      />

      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </DetailView>
  );
};

export default DepartmentView;
